import {
    enableProdMode,
    Injector,
    APP_INITIALIZER,
    LOCALE_ID,
    DEFAULT_CURRENCY_CODE,
    importProvidersFrom,
    provideZonelessChangeDetection,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpClientModule, HttpClientJsonpModule } from '@angular/common/http';
import { environment } from './environments/environment';
import { getRemoteServiceBaseUrl, appInitializerFactory, getCurrencyCode, getCurrentLanguage } from './root.module';
import { API_BASE_URL } from '@shared/service-proxies/service-proxies';
import { PlatformLocation } from '@angular/common';
import { NgxSpinnerModule } from 'ngx-spinner';
import { BrowserModule, bootstrapApplication } from '@angular/platform-browser';
import { SERVICE_PROXY_PROVIDERS } from '@shared/service-proxies/service-proxy.providers';
import { withInterceptorsFromDi, provideHttpClient } from '@angular/common/http';
import { provideRouter, withRouterConfig } from '@angular/router';
import { routes } from './root.routes';
import { RootComponent } from './root.component';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { providePrimeNG } from 'primeng/config';
import Lara from '@primeuix/themes/lara';
import { ModalModule } from 'ngx-bootstrap/modal';
import { TabsModule } from 'ngx-bootstrap/tabs';
import { PopoverModule } from 'ngx-bootstrap/popover';
import { IMaskModule } from 'angular-imask';
import { NgScrollbarModule } from 'ngx-scrollbar';
import { MonacoEditorModule } from 'ngx-monaco-editor-v2';
import { ImpersonationService } from '@app/admin/users/impersonation.service';
import { LinkedAccountService } from '@app/shared/layout/linked-account.service';
import { UserNotificationHelper } from '@app/shared/layout/notifications/UserNotificationHelper';
import { ChatSignalrService } from '@app/shared/layout/chat/chat-signalr.service';
import { RouterSpinnerService } from '@app/router-spinner.service';

if (environment.production) {
    enableProdMode();
}

// Configure Monaco workers to resolve language modules from an absolute base URL.
// The min/vs build ships a single workerMain.js that dynamically loads language code via AMD,
// so the worker needs its own MonacoEnvironment.baseUrl. We provide it via a blob proxy.
(window as any).MonacoEnvironment = {
    getWorkerUrl: () => {
        const base = `${window.location.origin}/assets/monaco/`;
        const proxy =
            `self.MonacoEnvironment = { baseUrl: '${base}' };` +
            `importScripts('${base}vs/base/worker/workerMain.js');`;
        return URL.createObjectURL(new Blob([proxy], { type: 'application/javascript' }));
    },
};

const bootstrap = () =>
    bootstrapApplication(RootComponent, {
        providers: [
            provideZonelessChangeDetection(),
            importProvidersFrom(
                BrowserModule,
                CommonModule,
                FormsModule,
                HttpClientModule,
                HttpClientJsonpModule,
                NgxSpinnerModule,
                ModalModule,
                TabsModule,
                PopoverModule,
                IMaskModule,
                NgScrollbarModule,
                MonacoEditorModule.forRoot({
                    baseUrl: 'assets/monaco/vs',
                })
            ),
            provideRouter(routes, withRouterConfig({ paramsInheritanceStrategy: 'emptyOnly' })),
            ...SERVICE_PROXY_PROVIDERS,
            ImpersonationService,
            LinkedAccountService,
            UserNotificationHelper,
            ChatSignalrService,
            RouterSpinnerService,
            { provide: API_BASE_URL, useFactory: getRemoteServiceBaseUrl },
            {
                provide: APP_INITIALIZER,
                useFactory: appInitializerFactory,
                deps: [Injector, PlatformLocation],
                multi: true,
            },
            {
                provide: APP_INITIALIZER,
                useFactory: appModuleInitializerFactory,
                multi: true,
            },
            {
                provide: LOCALE_ID,
                useFactory: getCurrentLanguage,
            },
            {
                provide: DEFAULT_CURRENCY_CODE,
                useFactory: getCurrencyCode,
                deps: [Injector],
            },
            provideHttpClient(withInterceptorsFromDi()),
            provideAnimationsAsync(),
            providePrimeNG({
                theme: {
                    preset: Lara,
                    options: {
                        darkModeSelector: '[data-bs-theme="dark"]',
                    },
                },
            }),
        ],
    });

function appModuleInitializerFactory() {
    return () => {
        const url = new URL(location.href);
        const params = url.searchParams;

        if (params.has('t')) {
            params.delete('t');
            window.location.href = url.toString();
        }
    };
}

bootstrap();
